import { PDF_LAYOUT_BLOCK_KEYS, PDF_LAYOUT_PATIENT_FIELD_KEYS } from '@/lib/api/pdf-layouts';
/** Default labels for the patientBlock whitelist keys (mirrors the backend). */
const PATIENT_FIELD_DEFAULT_LABELS = {
    patientName: 'Patient',
    species: 'Species',
    breed: 'Breed',
    patientAge: 'Age',
    ownerName: 'Owner',
    studyDescription: 'Study',
    modality: 'Modality',
    studyDate: 'Date',
    accessionNumber: 'Accession',
    referringVet: 'Referring DVM',
    institution: 'Institution',
};
// The legacy hardcoded patient block: order and enablement of the default
// render. "breed" and "patientAge" are whitelisted but were never rendered, so they default off.
const DEFAULT_ENABLED_ORDER = [
    'patientName',
    'species',
    'ownerName',
    'studyDescription',
    'modality',
    'studyDate',
    'accessionNumber',
    'referringVet',
    'institution',
];
function defaultPatientFields() {
    const rows = DEFAULT_ENABLED_ORDER.map((key) => ({
        key,
        label: PATIENT_FIELD_DEFAULT_LABELS[key],
        enabled: true,
    }));
    for (const key of PDF_LAYOUT_PATIENT_FIELD_KEYS) {
        if (!rows.some((r) => r.key === key)) {
            rows.push({ key, label: PATIENT_FIELD_DEFAULT_LABELS[key], enabled: false });
        }
    }
    return rows;
}
/** The built-in defaults = the legacy hardcoded PDF (the "Classic" look). */
export function defaultPdfLayoutForm() {
    return {
        pageSize: 'Letter',
        marginTopIn: 0.75,
        marginBottomIn: 1,
        marginHorizontalIn: 0.75,
        accentColor: '#3FB4A8',
        headingColor: '#062442',
        bodyColor: '#1E1E1E',
        mutedColor: '#787878',
        sectionLabelColor: '#505050',
        bodyFontSizePt: 11,
        headerTitle: 'RADIOLOGY REPORT',
        headerTitleSource: 'template',
        showLogo: true,
        headerArrangement: 'inline-left',
        headerTitleAlignment: 'left',
        logoSize: 'm',
        showCompanyName: true,
        showRule: true,
        patientFields: defaultPatientFields(),
        patientColumns: 2,
        sectionLabelStyle: 'uppercase',
        hideEmptySections: true,
        showDiagnosesSection: true,
        sectionLabelPlacement: 'above',
        sectionSpacing: 'normal',
        blockOrder: [...PDF_LAYOUT_BLOCK_KEYS],
        showPatientBlock: true,
        showImagesBlock: true,
        showSignatureBlock: true,
        showRequestBlock: true,
        requestShowRequester: true,
        requestHeader: 'REQUEST',
        responseHeader: 'RESPONSE',
        imageColumns: 1,
        showImageCaptions: true,
        signatureShowImage: true,
        signatureExtraLines: [],
        signatureShowTimestamp: true,
        footerShowPageNumbers: true,
        footerDisclaimer: '',
        draftWatermark: 'none',
    };
}
function isPatientFieldKey(key) {
    return PDF_LAYOUT_PATIENT_FIELD_KEYS.includes(key);
}
function isBlockKey(key) {
    return typeof key === 'string' && PDF_LAYOUT_BLOCK_KEYS.includes(key);
}
/**
 * Materialize a sparse wire config into the fully-populated form model.
 * Unknown keys are dropped; missing fields fall back to defaults — the same
 * leniency the backend renderer applies.
 */
export function configToForm(config) {
    const form = defaultPdfLayoutForm();
    if (!config)
        return form;
    const { page, theme, header, patientBlock, sections, images, request, signature, footer, draft } = config;
    if (page?.size === 'Letter' || page?.size === 'A4')
        form.pageSize = page.size;
    if (typeof page?.marginTopIn === 'number')
        form.marginTopIn = page.marginTopIn;
    if (typeof page?.marginBottomIn === 'number')
        form.marginBottomIn = page.marginBottomIn;
    if (typeof page?.marginHorizontalIn === 'number')
        form.marginHorizontalIn = page.marginHorizontalIn;
    if (theme?.accentColor)
        form.accentColor = theme.accentColor;
    if (theme?.headingColor)
        form.headingColor = theme.headingColor;
    if (theme?.bodyColor)
        form.bodyColor = theme.bodyColor;
    if (theme?.mutedColor)
        form.mutedColor = theme.mutedColor;
    if (theme?.sectionLabelColor)
        form.sectionLabelColor = theme.sectionLabelColor;
    if (typeof theme?.bodyFontSizePt === 'number')
        form.bodyFontSizePt = theme.bodyFontSizePt;
    if (typeof header?.title === 'string')
        form.headerTitle = header.title;
    if (header?.titleSource === 'template' || header?.titleSource === 'custom')
        form.headerTitleSource = header.titleSource;
    if (typeof header?.showLogo === 'boolean')
        form.showLogo = header.showLogo;
    // arrangement wins when present; legacy logoPosition right maps to
    // inline-right for configs saved before Phase 9.
    if (header?.arrangement === 'inline-left' ||
        header?.arrangement === 'inline-right' ||
        header?.arrangement === 'stacked-center') {
        form.headerArrangement = header.arrangement;
    }
    else if (header?.logoPosition === 'right') {
        form.headerArrangement = 'inline-right';
    }
    if (header?.titleAlignment === 'left' || header?.titleAlignment === 'center')
        form.headerTitleAlignment = header.titleAlignment;
    if (header?.logoSize === 's' || header?.logoSize === 'm' || header?.logoSize === 'l')
        form.logoSize = header.logoSize;
    if (typeof header?.showCompanyName === 'boolean')
        form.showCompanyName = header.showCompanyName;
    if (typeof header?.showRule === 'boolean')
        form.showRule = header.showRule;
    if (Array.isArray(patientBlock?.fields)) {
        const rows = [];
        for (const f of patientBlock.fields) {
            if (!f || !isPatientFieldKey(f.key) || rows.some((r) => r.key === f.key))
                continue;
            rows.push({
                key: f.key,
                label: f.label?.trim() ? f.label : PATIENT_FIELD_DEFAULT_LABELS[f.key],
                enabled: true,
            });
        }
        for (const key of PDF_LAYOUT_PATIENT_FIELD_KEYS) {
            if (!rows.some((r) => r.key === key)) {
                rows.push({ key, label: PATIENT_FIELD_DEFAULT_LABELS[key], enabled: false });
            }
        }
        form.patientFields = rows;
    }
    if (patientBlock?.columns === 1 || patientBlock?.columns === 2 || patientBlock?.columns === 3)
        form.patientColumns = patientBlock.columns;
    if (sections?.labelStyle === 'uppercase' || sections?.labelStyle === 'title')
        form.sectionLabelStyle = sections.labelStyle;
    if (typeof sections?.hideEmpty === 'boolean')
        form.hideEmptySections = sections.hideEmpty;
    if (typeof sections?.showDiagnoses === 'boolean')
        form.showDiagnosesSection = sections.showDiagnoses;
    if (sections?.labelPlacement === 'above' || sections?.labelPlacement === 'inline')
        form.sectionLabelPlacement = sections.labelPlacement;
    if (sections?.spacing === 'compact' ||
        sections?.spacing === 'normal' ||
        sections?.spacing === 'roomy')
        form.sectionSpacing = sections.spacing;
    // "sections" is the required backbone; the backend answers a stored list that
    // lost it with the full default order, so the designer shows the same.
    if (Array.isArray(config.blocks) && config.blocks.includes('sections')) {
        // Listed keys keep their order; unknown keys drop; unlisted keys append in
        // default order so every row still shows in the Structure editor (hidden
        // blocks lose their slot on the wire — same behavior as patient fields).
        // A Set keeps first-seen order, so it both de-duplicates and orders.
        const listed = new Set(config.blocks.filter(isBlockKey));
        const order = [...listed];
        // A v1 config predates the request block, so its absence there is not a
        // choice: give it its default slot (after the patient block) and show it,
        // exactly as the backend upgrades the same config when it renders.
        const upgradedRequest = (config.version ?? 1) < 2 && !listed.has('request');
        if (upgradedRequest) {
            const at = order.indexOf('patientBlock');
            order.splice(at >= 0 ? at + 1 : Math.max(0, order.indexOf('sections')), 0, 'request');
        }
        for (const key of PDF_LAYOUT_BLOCK_KEYS) {
            if (!order.includes(key))
                order.push(key);
        }
        form.blockOrder = order;
        form.showPatientBlock = listed.has('patientBlock');
        form.showImagesBlock = listed.has('images');
        form.showSignatureBlock = listed.has('signature');
        form.showRequestBlock = upgradedRequest || listed.has('request');
        // Contract: diagnoses absent from blocks = hidden even if showDiagnoses
        // was left on; surface the effective state so the single switch is honest.
        if (!listed.has('diagnoses'))
            form.showDiagnosesSection = false;
    }
    if (images?.defaultColumns === 1 || images?.defaultColumns === 2)
        form.imageColumns = images.defaultColumns;
    if (typeof images?.showCaptions === 'boolean')
        form.showImageCaptions = images.showCaptions;
    if (typeof request?.showRequester === 'boolean')
        form.requestShowRequester = request.showRequester;
    if (typeof request?.header === 'string')
        form.requestHeader = request.header;
    if (typeof request?.responseHeader === 'string')
        form.responseHeader = request.responseHeader;
    if (typeof signature?.showImage === 'boolean')
        form.signatureShowImage = signature.showImage;
    if (Array.isArray(signature?.extraLines)) {
        form.signatureExtraLines = signature.extraLines
            .filter((l) => typeof l === 'string' && l.trim().length > 0)
            .slice(0, 3)
            .map((l) => l.slice(0, 120));
    }
    if (typeof signature?.showSignedTimestamp === 'boolean')
        form.signatureShowTimestamp = signature.showSignedTimestamp;
    if (typeof footer?.showPageNumbers === 'boolean')
        form.footerShowPageNumbers = footer.showPageNumbers;
    if (typeof footer?.disclaimer === 'string')
        form.footerDisclaimer = footer.disclaimer;
    if (draft?.watermark === 'none' || draft?.watermark === 'diagonal')
        form.draftWatermark = draft.watermark;
    return form;
}
/**
 * Serialize the form model back to an explicit schema-v1 config. Fields the
 * designer does not edit (images.header, signature.timestampFormat,
 * draft.banner) are intentionally left absent so the backend defaults keep
 * applying — the designer never overwrites what it can't show.
 */
export function formToConfig(form) {
    return {
        version: 2,
        page: {
            size: form.pageSize,
            marginTopIn: form.marginTopIn,
            marginBottomIn: form.marginBottomIn,
            marginHorizontalIn: form.marginHorizontalIn,
        },
        theme: {
            accentColor: form.accentColor,
            headingColor: form.headingColor,
            bodyColor: form.bodyColor,
            mutedColor: form.mutedColor,
            sectionLabelColor: form.sectionLabelColor,
            bodyFontSizePt: form.bodyFontSizePt,
        },
        header: {
            title: form.headerTitle,
            titleSource: form.headerTitleSource,
            showLogo: form.showLogo,
            arrangement: form.headerArrangement,
            titleAlignment: form.headerTitleAlignment,
            logoSize: form.logoSize,
            showCompanyName: form.showCompanyName,
            showRule: form.showRule,
        },
        patientBlock: {
            fields: form.patientFields
                .filter((f) => f.enabled)
                .map((f) => ({ key: f.key, label: f.label })),
            columns: form.patientColumns,
        },
        sections: {
            labelStyle: form.sectionLabelStyle,
            hideEmpty: form.hideEmptySections,
            showDiagnoses: form.showDiagnosesSection,
            labelPlacement: form.sectionLabelPlacement,
            spacing: form.sectionSpacing,
        },
        images: {
            defaultColumns: form.imageColumns,
            showCaptions: form.showImageCaptions,
        },
        request: {
            showRequester: form.requestShowRequester,
            header: form.requestHeader,
            responseHeader: form.responseHeader,
        },
        signature: {
            showImage: form.signatureShowImage,
            extraLines: form.signatureExtraLines
                .map((l) => l.trim())
                .filter((l) => l.length > 0)
                .slice(0, 3)
                .map((l) => l.slice(0, 120)),
            showSignedTimestamp: form.signatureShowTimestamp,
        },
        footer: {
            showPageNumbers: form.footerShowPageNumbers,
            disclaimer: form.footerDisclaimer,
        },
        draft: {
            watermark: form.draftWatermark,
        },
        // sections is required so it always serializes; diagnoses keeps its order
        // slot even while switched off (sections.showDiagnoses is the one gate).
        blocks: form.blockOrder.filter((key) => {
            if (key === 'patientBlock')
                return form.showPatientBlock;
            if (key === 'images')
                return form.showImagesBlock;
            if (key === 'signature')
                return form.showSignatureBlock;
            if (key === 'request')
                return form.showRequestBlock;
            return true;
        }),
    };
}
export const PDF_LAYOUT_PRESETS = [
    {
        id: 'classic',
        apply: () => defaultPdfLayoutForm(),
    },
    {
        id: 'minimal',
        apply: () => ({
            ...defaultPdfLayoutForm(),
            headerTitle: 'IMAGING REPORT',
            showRule: false,
            accentColor: '#444444',
            headingColor: '#1E1E1E',
            bodyColor: '#1E1E1E',
            mutedColor: '#787878',
            sectionLabelColor: '#505050',
            headerArrangement: 'stacked-center',
            headerTitleAlignment: 'center',
            patientColumns: 1,
            sectionSpacing: 'compact',
        }),
    },
    {
        id: 'compact',
        apply: () => ({
            ...defaultPdfLayoutForm(),
            marginTopIn: 0.5,
            marginBottomIn: 0.75,
            marginHorizontalIn: 0.5,
            bodyFontSizePt: 9,
            imageColumns: 2,
            headerArrangement: 'inline-left',
            patientColumns: 2,
            sectionSpacing: 'compact',
        }),
    },
];
